import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Progress,
  Icon,
  Collapse,
  Button,
  Spinner,
  Divider,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip
} from '@chakra-ui/react';
import {
  Eye,
  TrendingUp,
  Brain,
  ChevronDown,
  ChevronUp,
  Search,
  Sparkles,
  RotateCcw
} from 'lucide-react';
import { userPreferencesApi } from '../../../apis/user-preferences';

interface AlgorithmPreviewProps {
  description: string;
  isVisible: boolean;
  onWeightChange?: (weightKey: string, value: number) => void;
  manualWeights?: Record<string, number>;
  onAiWeightsUpdate?: (weights: Record<string, number>) => void;
}

interface WeightPreview {
  name: string;
  value: number;
  change: 'increased' | 'decreased' | 'neutral';
  impact: string;
  key: string;
}

interface EmbeddingInsight {
  topicKeywords: string[];
  contentTypes: string[];
  stylePreferences: string[];
}

export const AlgorithmPreview: React.FC<AlgorithmPreviewProps> = ({
  description,
  isVisible,
  onWeightChange,
  manualWeights = {},
  onAiWeightsUpdate
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [weightPreviews, setWeightPreviews] = useState<WeightPreview[]>([]);
  const [embeddingInsights, setEmbeddingInsights] = useState<EmbeddingInsight | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiSuggestedWeights, setAiSuggestedWeights] = useState<Record<string, number>>({});

  // Real AI analysis of the description
  useEffect(() => {
    if (!description.trim() || description.length < 20) {
      setWeightPreviews([]);
      setEmbeddingInsights(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const analyzeDescription = async () => {
      try {
        // Call the real API to parse instruction components
        const components = await userPreferencesApi.parseInstructionComponents(description);
        console.log('components', components);
        // Store AI suggested weights
        const aiWeights = components.suggestedWeights || {};
        setAiSuggestedWeights(aiWeights);

        // Notify parent component about AI weights
        if (onAiWeightsUpdate) {
          onAiWeightsUpdate(aiWeights);
        }

        // Convert weights to preview format (combine AI suggestions with manual adjustments)
        const combinedWeights = { ...aiWeights, ...manualWeights };
        const previews = convertWeightsToPreview(combinedWeights || {});
        setWeightPreviews(previews);

        // Generate embedding insights
        const insights = generateEmbeddingInsights(components);
        setEmbeddingInsights(insights);
      } catch (error) {
        console.error('Failed to analyze description:', error);
        setError('Unable to analyze description. Please try again.');
        // Fallback to mock analysis
        const mockAnalysis = analyzeMockDescription(description);
        setWeightPreviews(mockAnalysis);
        setEmbeddingInsights(generateMockEmbeddingInsights(description));
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the API call
    const timer = setTimeout(analyzeDescription, 2000);
    return () => clearTimeout(timer);
  }, [description]);

  // Update weight previews when manual weights change
  useEffect(() => {
    if (Object.keys(aiSuggestedWeights).length > 0) {
      const combinedWeights = { ...aiSuggestedWeights, ...manualWeights };
      const previews = convertWeightsToPreview(combinedWeights);
      setWeightPreviews(previews);
    }
  }, [manualWeights, aiSuggestedWeights]);

  // Helper function to determine change type
  const getChangeType = (value: number): 'increased' | 'decreased' | 'neutral' => {
    const tolerance = 0.05; // Small tolerance for floating point comparison
    if (value > 1.0 + tolerance) return 'increased';
    if (value < 1.0 - tolerance) return 'decreased';
    return 'neutral';
  };

  // Helper function to create intuitive weight display
  const getWeightDisplay = (value: number, change: 'increased' | 'decreased' | 'neutral') => {
    const amplification = Math.abs(value - 1.0);

    if (change === 'neutral') {
      return (
        <Badge colorScheme="blue" variant="subtle" fontSize="xs" bg="white" color="gray.600">
          Unchanged
        </Badge>
      );
    }

    // Determine amplification level
    let level = '';
    let intensity = '';

    if (amplification <= 0.2) {
      level = 'Slight';
      intensity = 'subtle';
    } else if (amplification <= 0.4) {
      level = 'Moderate';
      intensity = 'solid';
    } else if (amplification <= 0.6) {
      level = 'Strong';
      intensity = 'solid';
    } else {
      level = 'Maximum';
      intensity = 'solid';
    }

    const colorScheme = change === 'increased' ? 'green' : 'blue'; // Use blue instead of red
    const symbol = change === 'increased' ? '→' : '←';

    return (
      <HStack spacing={1}>
        <Badge
          colorScheme={colorScheme}
          variant={intensity}
          fontSize="xs"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <span>{symbol}</span>
          <span>{level}</span>
        </Badge>
      </HStack>
    );
  };

  // Helper function to convert weight value to intuitive progress bar value
  const getProgressValue = (value: number): number => {
    // Map weight values to a 0-100 scale where 50 = balanced (1.0)
    // We want: 0.5 → 0%, 1.0 → 50%, 1.5 → 100%
    if (value <= 1.0) {
      // For values 0.5 to 1.0, map to 0-50%
      return ((value - 0.5) / 0.5) * 50;
    } else {
      // For values 1.0 to 1.5, map to 50-100%
      return 50 + ((value - 1.0) / 0.5) * 50;
    }
  };

  // Helper function to get specific progress bar labels for each weight type
  const getProgressLabel = (weightKey: string, position: 'low' | 'high'): string => {
    const labels: Record<string, { low: string; high: string }> = {
      educationalValueWeight: {
        low: 'Entertainment',
        high: 'Educational'
      },
      lightheartednessWeight: {
        low: 'Very serious',
        high: 'Lighthearted'
      },
      contentQualityWeight: {
        low: 'Poor quality',
        high: 'High quality'
      },
      contentTrustworthinessWeight: {
        low: 'Unreliable',
        high: 'Trustworthy'
      },
      clickbaitWeight: {
        low: 'Allow clickbait',
        high: 'Block clickbait'
      },
      integrityWeight: {
        low: 'Bad faith',
        high: 'Good faith'
      },
      civilityWeight: {
        low: 'Uncivil',
        high: 'Respectful'
      },
      recencyWeight: {
        low: 'Timeless',
        high: 'Recent'
      },
      perspectiveDiversityWeight: {
        low: 'One perspective',
        high: 'Many perspectives'
      },
      originalityWeight: {
        low: 'Derivative',
        high: 'Original'
      },
      sentimentWeight: {
        low: 'Negative',
        high: 'Positive'
      },
      embeddingSimilarityWeight: {
        low: 'Broad match',
        high: 'Exact match'
      }
    };

    return labels[weightKey]?.[position] || (position === 'low' ? 'Less' : 'More');
  };

  // Convert real API weights to preview format
  const convertWeightsToPreview = (weights: Record<string, number>): WeightPreview[] => {
    const weightNames: Record<string, string> = {
      educationalValueWeight: 'Educational Value',
      lightheartednessWeight: 'Lightheartedness',
      contentQualityWeight: 'Content Quality',
      contentTrustworthinessWeight: 'Trustworthiness',
      clickbaitWeight: 'Clickbait Resistance',
      integrityWeight: 'Content Integrity',
      civilityWeight: 'Civility',
      recencyWeight: 'Recency',
      perspectiveDiversityWeight: 'Perspective Diversity',
      originalityWeight: 'Originality',
      sentimentWeight: 'Sentiment',
      embeddingSimilarityWeight: 'Topic Matching'
    };

    const weightImpacts: Record<string, string> = {
      educationalValueWeight: 'Tutorials, guides, and informative content',
      lightheartednessWeight: 'Casual, entertaining, and humorous content',
      contentQualityWeight: 'Well-researched, well-written articles',
      contentTrustworthinessWeight: 'Factually accurate, source-backed content',
      clickbaitWeight: 'Filtering of sensationalist headlines',
      integrityWeight: 'Content with fair, good-faith arguments',
      civilityWeight: 'Respectful, constructive discussions',
      recencyWeight: 'Newer articles over older content',
      perspectiveDiversityWeight: 'Content considering multiple viewpoints',
      originalityWeight: 'Unique insights and fresh perspectives',
      sentimentWeight: 'Positive, uplifting content',
      embeddingSimilarityWeight: 'Content matching your specific topics and interests'
    };

    // Create a complete list of all possible weights with platform-focused defaults
    const allWeights = {
      educationalValueWeight: 1.0, // Keep neutral - don't punish natural writing
      lightheartednessWeight: 1.0, // Keep neutral
      contentQualityWeight: 1.0, // Keep neutral - don't punish natural writing
      contentTrustworthinessWeight: 1.0, // Keep neutral for now
      clickbaitWeight: 1.3, // Actively reduce clickbait content
      integrityWeight: 1.2, // Promote good-faith arguments
      civilityWeight: 1.3, // Actively promote civil discourse
      recencyWeight: 1.0, // Keep neutral (often overridden)
      perspectiveDiversityWeight: 1.0, // Keep neutral
      originalityWeight: 1.0, // Keep neutral
      sentimentWeight: 1.0, // Keep neutral
      embeddingSimilarityWeight: 1.0, // Frontend default (backend scales this)
      ...weights // Override with actual weights from API
    };

    return Object.entries(allWeights)
      .map(([key, value]) => ({
        name: weightNames[key] || key,
        value,
        change: getChangeType(value),
        impact: weightImpacts[key] || 'Affects content ranking',
        key
      }))
      .sort((a, b) => {
        // Use stable sorting based on weight key to prevent re-ordering during updates
        return a.key.localeCompare(b.key);
      });
  };

  // Generate embedding insights from parsed components
  const generateEmbeddingInsights = (components: any): EmbeddingInsight => {
    return {
      topicKeywords: components.topics || [],
      contentTypes: components.tones || [],
      stylePreferences: extractStylePreferences(components.topicForEmbedding || '')
    };
  };

  const extractStylePreferences = (text: string): string[] => {
    const lower = text.toLowerCase();
    const preferences: string[] = [];

    if (lower.includes('educational') || lower.includes('learn')) preferences.push('Educational');
    if (lower.includes('funny') || lower.includes('humor')) preferences.push('Humorous');
    if (lower.includes('serious') || lower.includes('professional'))
      preferences.push('Professional');
    if (lower.includes('casual') || lower.includes('lighthearted')) preferences.push('Casual');
    if (lower.includes('in-depth') || lower.includes('detailed')) preferences.push('In-depth');
    if (lower.includes('quick') || lower.includes('brief')) preferences.push('Concise');

    return preferences;
  };

  const generateMockEmbeddingInsights = (desc: string): EmbeddingInsight => {
    const lower = desc.toLowerCase();
    const insights: EmbeddingInsight = {
      topicKeywords: [],
      contentTypes: [],
      stylePreferences: []
    };

    // Extract topics
    if (lower.includes('tech')) insights.topicKeywords.push('technology');
    if (lower.includes('business')) insights.topicKeywords.push('business');
    if (lower.includes('health')) insights.topicKeywords.push('health');
    if (lower.includes('science')) insights.topicKeywords.push('science');
    if (lower.includes('politics')) insights.topicKeywords.push('politics');

    // Extract content types
    if (lower.includes('news')) insights.contentTypes.push('news articles');
    if (lower.includes('tutorial')) insights.contentTypes.push('tutorials');
    if (lower.includes('analysis')) insights.contentTypes.push('analysis pieces');
    if (lower.includes('opinion')) insights.contentTypes.push('opinion articles');

    // Extract style preferences
    insights.stylePreferences = extractStylePreferences(desc);

    return insights;
  };

  // Mock analysis function - fallback when API fails
  const analyzeMockDescription = (desc: string): WeightPreview[] => {
    const lower = desc.toLowerCase();

    // Start with platform-focused default weights
    const mockWeights: Record<string, number> = {
      educationalValueWeight: 1.0, // Keep neutral - don't punish natural writing
      lightheartednessWeight: 1.0, // Keep neutral
      contentQualityWeight: 1.0, // Keep neutral - don't punish natural writing
      contentTrustworthinessWeight: 1.0, // Keep neutral for now
      clickbaitWeight: 1.3, // Actively reduce clickbait content
      integrityWeight: 1.2, // Promote good-faith arguments
      civilityWeight: 1.3, // Actively promote civil discourse
      recencyWeight: 1.0, // Keep neutral (often overridden)
      perspectiveDiversityWeight: 1.0, // Keep neutral
      originalityWeight: 1.0, // Keep neutral
      sentimentWeight: 1.0, // Keep neutral
      embeddingSimilarityWeight: 1.0 // Frontend default (backend scales this)
    };

    // Adjust weights based on description content
    if (lower.includes('educational') || lower.includes('learn') || lower.includes('knowledge')) {
      mockWeights.educationalValueWeight = 1.4;
    }
    if (
      lower.includes('funny') ||
      lower.includes('humor') ||
      lower.includes('lighthearted') ||
      lower.includes('fun')
    ) {
      mockWeights.lightheartednessWeight = 1.3;
    }
    if (lower.includes('quality') || lower.includes('well-written') || lower.includes('in-depth')) {
      mockWeights.contentQualityWeight = 1.3;
    }
    if (lower.includes('trustworthy') || lower.includes('reliable') || lower.includes('credible')) {
      mockWeights.contentTrustworthinessWeight = 1.4;
    }
    if (
      lower.includes('balanced') ||
      lower.includes('diverse') ||
      lower.includes('multiple perspectives')
    ) {
      mockWeights.perspectiveDiversityWeight = 1.3;
    }
    if (
      lower.includes('recent') ||
      lower.includes('breaking') ||
      lower.includes('latest') ||
      lower.includes('news')
    ) {
      mockWeights.recencyWeight = 1.2;
    }
    if (
      lower.includes('avoid clickbait') ||
      lower.includes('no clickbait') ||
      lower.includes('not sensational')
    ) {
      mockWeights.clickbaitWeight = 1.4;
    }

    // Use the same conversion logic as the real API
    return convertWeightsToPreview(mockWeights);
  };

  const renderWeightItem = (weight: WeightPreview, index: number, isUnchanged = false) => {
    const currentValue = manualWeights[weight.key] ?? weight.value;
    const aiSuggestedValue = aiSuggestedWeights[weight.key] ?? 1.0;
    const isManuallyAdjusted = manualWeights[weight.key] !== undefined;
    const hasAiSuggestion = Math.abs(aiSuggestedValue - 1.0) > 0.05;
    const wasUnchangedButModified = isUnchanged && isManuallyAdjusted;

    return (
      <Box
        key={`weight-${weight.key}`}
        p={3}
        bg={wasUnchangedButModified ? 'blue.50' : isUnchanged ? 'gray.50' : 'white'}
        borderRadius="md"
        borderWidth={wasUnchangedButModified ? 2 : 1}
        borderColor={wasUnchangedButModified ? 'blue.300' : isUnchanged ? 'gray.200' : 'blue.100'}
        position="relative"
        boxShadow={wasUnchangedButModified ? '0 0 0 3px rgba(66, 153, 225, 0.1)' : 'none'}
        transition="all 0.2s ease-in-out"
      >
        <HStack justify="space-between" mb={2}>
          <HStack spacing={2}>
            <Icon
              as={TrendingUp}
              color={
                isUnchanged
                  ? 'gray.400'
                  : getChangeType(currentValue) === 'increased'
                    ? 'green.500'
                    : getChangeType(currentValue) === 'decreased'
                      ? 'blue.500'
                      : 'gray.500'
              }
              boxSize={4}
            />
            <Text fontWeight="medium" fontSize="sm" color={isUnchanged ? 'gray.600' : undefined}>
              {weight.name}
            </Text>
            {isManuallyAdjusted && (
              <Badge
                colorScheme={wasUnchangedButModified ? 'blue' : 'purple'}
                variant={wasUnchangedButModified ? 'solid' : 'subtle'}
                fontSize="xs"
              >
                {wasUnchangedButModified ? 'You Modified' : 'Manual'}
              </Badge>
            )}
          </HStack>
          <HStack spacing={2}>
            {getWeightDisplay(currentValue, getChangeType(currentValue))}
            {/* Reset to AI suggestion button */}
            {hasAiSuggestion && isManuallyAdjusted && onWeightChange && (
              <Tooltip label={`Reset to AI suggestion (${Math.round(aiSuggestedValue * 100)}%)`}>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => onWeightChange(weight.key, aiSuggestedValue)}
                  _hover={{ bg: 'gray.100' }}
                >
                  <Icon as={RotateCcw} boxSize={3} />
                </Button>
              </Tooltip>
            )}
          </HStack>
        </HStack>

        <Text fontSize="xs" color={isUnchanged ? 'gray.500' : 'gray.600'} mb={3}>
          {weight.impact}
        </Text>

        {/* Interactive Slider */}
        {onWeightChange && (
          <VStack spacing={2} align="stretch">
            <Box position="relative">
              <Slider
                value={currentValue}
                min={0.5}
                max={1.5}
                step={0.1}
                onChange={(value) => onWeightChange(weight.key, value)}
                colorScheme={
                  getChangeType(currentValue) === 'increased'
                    ? 'green'
                    : getChangeType(currentValue) === 'decreased'
                      ? 'blue'
                      : 'gray'
                }
              >
                <SliderTrack bg="gray.200" borderRadius="full">
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={4} bg="white" border="2px solid" borderColor="currentColor" />
              </Slider>

              {/* AI suggestion marker */}
              {hasAiSuggestion && (
                <Box
                  position="absolute"
                  top="50%"
                  transform="translateY(-50%)"
                  left={`${((aiSuggestedValue - 0.5) / 1.0) * 100}%`}
                  width="3px"
                  height="12px"
                  bg="blue.400"
                  borderRadius="full"
                  zIndex={1}
                  pointerEvents="none"
                />
              )}
            </Box>

            {/* Slider labels */}
            <Box position="relative">
              <HStack justify="space-between">
                <Text
                  fontSize="xs"
                  color={isUnchanged ? 'gray.400' : 'gray.500'}
                  textAlign="left"
                  flex="1"
                >
                  {getProgressLabel(weight.key, 'low')}
                </Text>
                <Text
                  fontSize="xs"
                  color={isUnchanged ? 'gray.400' : 'gray.500'}
                  textAlign="right"
                  flex="1"
                >
                  {getProgressLabel(weight.key, 'high')}
                </Text>
              </HStack>
              {/* Centered "Unchanged" label */}
              <Box position="absolute" top="0" left="50%" transform="translateX(-50%)">
                <Text fontSize="xs" color="gray.600" fontWeight="medium" textAlign="center">
                  Unchanged
                </Text>
              </Box>
            </Box>
          </VStack>
        )}

        {/* Non-interactive version for display only */}
        {!onWeightChange && (
          <Box mt={2}>
            <Box position="relative">
              <Progress
                value={Math.max(8, getProgressValue(currentValue))}
                size="sm"
                colorScheme={
                  isUnchanged
                    ? 'gray'
                    : getChangeType(currentValue) === 'increased'
                      ? 'green'
                      : getChangeType(currentValue) === 'decreased'
                        ? 'blue'
                        : 'gray'
                }
                borderRadius="full"
                min={0}
                max={100}
              />
              {/* Standard line marker at 50% */}
              <Box
                position="absolute"
                top="0"
                left="50%"
                transform="translateX(-50%)"
                width="2px"
                height="100%"
                bg="gray.400"
                borderRadius="full"
                opacity={0.6}
              />
            </Box>
            {/* Progress bar labels with proper alignment */}
            <Box position="relative" mt={1}>
              <HStack justify="space-between">
                <Text
                  fontSize="xs"
                  color={isUnchanged ? 'gray.400' : 'gray.500'}
                  textAlign="left"
                  flex="1"
                >
                  {getProgressLabel(weight.key, 'low')}
                </Text>
                <Text
                  fontSize="xs"
                  color={isUnchanged ? 'gray.400' : 'gray.500'}
                  textAlign="right"
                  flex="1"
                >
                  {getProgressLabel(weight.key, 'high')}
                </Text>
              </HStack>
              {/* Centered "Unchanged" label */}
              <Box position="absolute" top="0" left="50%" transform="translateX(-50%)">
                <Text fontSize="xs" color="gray.600" fontWeight="medium" textAlign="center">
                  Unchanged
                </Text>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  if (!isVisible || !description.trim() || description.length < 20) {
    return null;
  }

  // Separate weights based on ORIGINAL AI suggestions, not current values
  const originallyChangedWeights = weightPreviews.filter((w) => {
    const originalValue = aiSuggestedWeights[w.key] ?? 1.0;
    return Math.abs(originalValue - 1.0) > 0.05;
  });

  const originallyUnchangedWeights = weightPreviews.filter((w) => {
    const originalValue = aiSuggestedWeights[w.key] ?? 1.0;
    return Math.abs(originalValue - 1.0) <= 0.05;
  });

  return (
    <Box
      mt={{ base: 3, md: 4 }}
      p={{ base: 3, md: 4 }}
      borderRadius="lg"
      borderWidth={1}
      borderColor="blue.200"
      bg="blue.50"
    >
      <HStack justify="space-between" mb={3}>
        <HStack spacing={2}>
          <Icon as={Brain} color="blue.500" boxSize={{ base: 4, md: 5 }} />
          <Text fontWeight="bold" color="blue.800" fontSize={{ base: 'sm', md: 'md' }}>
            AI Analysis Preview
          </Text>
          <Badge colorScheme="blue" variant="subtle" fontSize={{ base: 'xs', md: 'xs' }}>
            Live
          </Badge>
        </HStack>
        <Button
          size={{ base: 'xs', md: 'sm' }}
          variant="ghost"
          _hover={{ bg: 'green.50' }}
          onClick={() => setIsExpanded(!isExpanded)}
          rightIcon={isExpanded ? <ChevronUp /> : <ChevronDown />}
          fontSize={{ base: 'xs', md: 'sm' }}
        >
          {isExpanded ? 'Less' : 'More'}
        </Button>
      </HStack>

      {/* Always visible summary */}
      {isLoading ? (
        <HStack spacing={3}>
          <Spinner size="sm" color="blue.500" />
          <Text fontSize="sm" color="blue.700">
            Analyzing your preferences...
          </Text>
        </HStack>
      ) : (
        <VStack spacing={3} align="stretch">
          {originallyChangedWeights.length > 0 ? (
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="blue.700">
              {originallyChangedWeights.length} algorithm weights will be customized for your
              preferences
            </Text>
          ) : (
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="blue.700">
              Add more specific preferences to see algorithm customizations
            </Text>
          )}
        </VStack>
      )}

      {/* Expanded content */}
      <Collapse in={isExpanded} animateOpacity>
        <VStack spacing={4} align="stretch" mt={4}>
          <Text fontSize="sm" color="blue.700" fontWeight="medium">
            Here's how your feed algorithm will be configured:
          </Text>

          <VStack spacing={3} align="stretch">
            {/* All weights in one continuous section */}
            <VStack spacing={2} align="stretch">
              {/* Originally changed weights first */}
              {originallyChangedWeights.length > 0 && (
                <>
                  <Text fontSize="sm" color="blue.800" fontWeight="medium">
                    🎯 AI Customized Weights
                  </Text>
                  {originallyChangedWeights.map((weight, index) =>
                    renderWeightItem(weight, index, false)
                  )}
                </>
              )}

              {/* Originally unchanged weights button and section */}
              {originallyUnchangedWeights.length > 0 && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    _hover={{ bg: 'green.50' }}
                    onClick={() => setShowUnchanged(!showUnchanged)}
                    rightIcon={showUnchanged ? <ChevronUp /> : <ChevronDown />}
                    mt={originallyChangedWeights.length > 0 ? 2 : 0}
                  >
                    {showUnchanged ? 'Hide' : 'Show'} standard weights (
                    {originallyUnchangedWeights.length})
                  </Button>

                  <Collapse in={showUnchanged} animateOpacity>
                    <VStack spacing={2} align="stretch">
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        ⚖️ Standard Weights
                      </Text>
                      {originallyUnchangedWeights.map((weight, index) =>
                        renderWeightItem(weight, index, true)
                      )}
                    </VStack>
                  </Collapse>
                </>
              )}
            </VStack>

            {/* Content Discovery Preview - Always at the bottom */}
            {embeddingInsights &&
              (embeddingInsights.topicKeywords.length > 0 ||
                embeddingInsights.contentTypes.length > 0 ||
                embeddingInsights.stylePreferences.length > 0) && (
                <>
                  <Divider />
                  <Box
                    p={3}
                    bg="purple.50"
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderColor="purple.400"
                  >
                    <HStack spacing={2} mb={2}>
                      <Icon as={Search} color="purple.500" boxSize={4} />
                      <Text fontSize="sm" fontWeight="medium" color="purple.800">
                        Content Discovery Preview
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="purple.700" mb={3}>
                      Based on your description, we'll prioritize articles with these
                      characteristics:
                    </Text>

                    <VStack spacing={2} align="stretch">
                      {embeddingInsights.topicKeywords.length > 0 && (
                        <Box>
                          <Text fontSize="xs" fontWeight="medium" color="purple.800" mb={1}>
                            📚 Topics & Subjects:
                          </Text>
                          <HStack spacing={1} flexWrap="wrap">
                            {embeddingInsights.topicKeywords.map((topic, idx) => (
                              <Badge key={idx} colorScheme="purple" variant="subtle" fontSize="xs">
                                {topic}
                              </Badge>
                            ))}
                          </HStack>
                        </Box>
                      )}

                      {embeddingInsights.stylePreferences.length > 0 && (
                        <Box>
                          <Text fontSize="xs" fontWeight="medium" color="purple.800" mb={1}>
                            🎨 Style & Tone:
                          </Text>
                          <HStack spacing={1} flexWrap="wrap">
                            {embeddingInsights.stylePreferences.map((style, idx) => (
                              <Badge key={idx} colorScheme="blue" variant="subtle" fontSize="xs">
                                {style}
                              </Badge>
                            ))}
                          </HStack>
                        </Box>
                      )}

                      {embeddingInsights.contentTypes.length > 0 && (
                        <Box>
                          <Text fontSize="xs" fontWeight="medium" color="purple.800" mb={1}>
                            📝 Content Types:
                          </Text>
                          <HStack spacing={1} flexWrap="wrap">
                            {embeddingInsights.contentTypes.map((type, idx) => (
                              <Badge key={idx} colorScheme="cyan" variant="subtle" fontSize="xs">
                                {type}
                              </Badge>
                            ))}
                          </HStack>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </>
              )}

            {/* Show message when no analysis available */}
            {weightPreviews.length === 0 && (
              <Box p={3} bg="white" borderRadius="md" borderWidth={1} borderColor="blue.100">
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Add more specific preferences to see algorithm customizations
                </Text>
              </Box>
            )}
          </VStack>

          {/* Error State */}
          {error && (
            <Box p={3} bg="red.50" borderRadius="md" borderLeft="4px solid" borderColor="red.400">
              <Text fontSize="sm" fontWeight="medium" color="red.800" mb={1}>
                ⚠️ Analysis Error
              </Text>
              <Text fontSize="xs" color="red.700">
                {error}
              </Text>
            </Box>
          )}

          <Box p={3} bg="green.50" borderRadius="md" borderLeft="4px solid" borderColor="green.400">
            <Text fontSize="sm" fontWeight="medium" color="green.800" mb={1}>
              💡 Pro Tip
            </Text>
            <Text fontSize="xs" color="green.700">
              The more specific your description, the better we can optimize your feed. Try
              mentioning topics, tone preferences, or content types you enjoy.
            </Text>
          </Box>
        </VStack>
      </Collapse>
    </Box>
  );
};
